import {
  ArrowsClockwise,
  Barcode,
  ListBullets,
  Package,
  Pencil,
  PencilSimple,
  PencilSimpleLine,
  Stack,
  Trash,
} from '@phosphor-icons/react';
import { Image } from 'semantic-ui-react';
import { HoverRowActionsDropdown } from '@components/HoverRowActionsDropdown';
import { DualIcon } from '../../../elements';
import { Images } from '../../../images';
import { COLOR } from '@styles/theme';
import type { InventoryListApiItem, LocationSummary } from './inventoryListTypes';

type ProductActionsProps = {
  item: InventoryListApiItem;
  openPopup: (
    item: InventoryListApiItem & { lotIndex: number; transferLot?: boolean },
  ) => void;
  editItem: (item: InventoryListApiItem) => void;
  editBarcodes: (item: InventoryListApiItem) => void;
  editRestock: (item: InventoryListApiItem) => void;
  openItemPopupLogs: (item: InventoryListApiItem) => void;
};

export function InventoryProductRowActions({
  item,
  openPopup,
  editItem,
  editBarcodes,
  editRestock,
  openItemPopupLogs,
}: ProductActionsProps) {
  const items = [
    {
      key: 'edit-qty',
      label: 'Edit Quantity',
      icon: <Package size={18} />,
    },
    {
      key: 'transfer',
      label: 'Transfer',
      icon: <Image src={Images.Icons.transferLogo} style={{ width: 18, height: 18 }} />,
      disabled: !item.quantity?.available,
    },
    {
      key: 'edit-carton',
      label: 'Edit Carton Configuration',
      icon: <Pencil size={18} />,
    },
    {
      key: 'edit-barcodes',
      label: 'Edit Barcode Identifiers',
      icon: <Barcode size={18} />,
    },
    {
      key: 'edit-restock',
      label: 'Edit Re-Stock Threshold',
      icon: <ArrowsClockwise size={18} />,
    },
    {
      key: 'export-logs',
      label: 'Export Logs',
      icon: <ListBullets size={18} />,
    },
  ];

  const onAction = (key: string) => {
    switch (key) {
      case 'edit-qty':
        openPopup({ ...item, lotIndex: -1 });
        break;
      case 'transfer':
        openPopup({ ...item, lotIndex: -1, transferLot: true });
        break;
      case 'edit-carton':
        editItem(item);
        break;
      case 'edit-barcodes':
        editBarcodes(item);
        break;
      case 'edit-restock':
        editRestock(item);
        break;
      case 'export-logs':
        openItemPopupLogs(item);
        break;
      default:
        break;
    }
  };

  return <HoverRowActionsDropdown items={items} onAction={onAction} />;
}

type LotActionsProps = {
  parentItem: InventoryListApiItem;
  lot: LocationSummary;
  lotIndex: number;
  openPopup: (
    item: InventoryListApiItem & { lotIndex: number; transferLot?: boolean },
  ) => void;
  editLotAttribute: (lot: LocationSummary & { itemId?: string }) => void;
  editPallet: (payload: { lot: LocationSummary } & InventoryListApiItem) => void;
  onDeleteLot: (lot: LocationSummary) => void;
};

export function InventoryLotRowActions({
  parentItem,
  lot,
  lotIndex,
  openPopup,
  editLotAttribute,
  editPallet,
  onDeleteLot,
}: LotActionsProps) {
  const items = [
    {
      key: 'edit-qty',
      label: 'Edit Quantity',
      icon: <Pencil size={18} />,
    },
    {
      key: 'transfer',
      label: 'Transfer',
      icon: <Image src={Images.Icons.transferLogo} style={{ width: 18, height: 18 }} />,
      disabled: !lot.quantity?.available,
    },
    {
      key: 'edit-carton-attrs',
      label: 'Edit Carton Attributes',
      icon: <PencilSimpleLine size={18} />,
    },
    {
      key: 'edit-pallet',
      label: 'Edit Pallet',
      icon: <DualIcon iconBehind={<Stack />} iconFront={<PencilSimple />} size={18} />,
    },
    {
      key: 'delete',
      label: 'Delete Lot',
      icon: <Trash size={18} color={COLOR.danger} />,
      danger: true,
    },
  ];

  const onAction = (key: string) => {
    switch (key) {
      case 'edit-qty':
        openPopup({ ...parentItem, lotIndex });
        break;
      case 'transfer':
        openPopup({ ...parentItem, lotIndex, transferLot: true });
        break;
      case 'edit-carton-attrs':
        editLotAttribute({ ...lot, itemId: parentItem.productId });
        break;
      case 'edit-pallet':
        editPallet({ ...parentItem, lot });
        break;
      case 'delete':
        onDeleteLot(lot);
        break;
      default:
        break;
    }
  };

  return <HoverRowActionsDropdown items={items} onAction={onAction} />;
}
